import { useRef, useState, type ChangeEvent } from 'react';
import { CheckIcon, FileIcon, UploadCloudIcon } from './icons';
import { useI18n } from '../i18n';
import { formatBytes } from '../lib/utils';

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Shared, keyboard-accessible picker for publishing and updating. */
export default function UploadField({ file, onChange, disabled = false, id = 'project-file' }: {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  id?: string;
}) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  function selectFile(next: File | undefined) {
    if (!next || disabled) return;
    if (!next.name.toLowerCase().endsWith('.zip')) {
      onChange(null);
      setError(t('create.errZipOnly'));
      return;
    }
    if (next.size === 0 || next.size > MAX_UPLOAD_BYTES) {
      onChange(null);
      setError(t(next.size === 0 ? 'workspace.fileEmpty' : 'workspace.fileLarge'));
      return;
    }
    setError('');
    onChange(next);
  }

  function handleInput(event: ChangeEvent<HTMLInputElement>) {
    selectFile(event.target.files?.[0]);
    event.target.value = '';
  }

  return (
    <div className="upload-field">
      <input ref={inputRef} id={id} className="visually-hidden" type="file" accept=".zip,application/zip"
        disabled={disabled} onChange={handleInput} aria-describedby={`${id}-hint${error ? ` ${id}-error` : ''}`}
        aria-invalid={!!error} aria-label={t('create.zipLabel')} />
      <div className={`upload-target${dragOver ? ' is-dragging' : ''}${file ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
        onDragOver={(event) => { event.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => { event.preventDefault(); setDragOver(false); selectFile(event.dataTransfer.files[0]); }}>
        {file ? (
          <>
            <div className="file-selected">
              <span className="file-symbol"><FileIcon size={24} /></span>
              <div><strong>{file.name}</strong><span>{formatBytes(file.size)}</span></div>
              <CheckIcon size={20} />
            </div>
            <div className="upload-file-actions">
              <button type="button" className="text-button" disabled={disabled} onClick={() => inputRef.current?.click()}>{t('workspace.changeFile')}</button>
              <button type="button" className="text-button" disabled={disabled} onClick={() => { onChange(null); setError(''); }}>{t('common.remove')}</button>
            </div>
          </>
        ) : (
          <>
            <span className="upload-symbol"><UploadCloudIcon size={28} /></span>
            <h3>{t('create.dropTitle')}</h3>
            <p>{t('workspace.uploadOr')}</p>
            <button type="button" className="btn btn-ghost" disabled={disabled} onClick={() => inputRef.current?.click()}>{t('workspace.chooseFile')}</button>
          </>
        )}
      </div>
      <p id={`${id}-hint`} className="form-hint upload-hint">{t('create.dropHint')}</p>
      {error && <p id={`${id}-error`} className="field-error" role="alert">{error}</p>}
    </div>
  );
}
